import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import { getIngestionJobsUrl, getIngestionJobDeleteUrl, getBuildToeflQuizUrl } from '../../utils/UrlUtilities';
import { formatBdPst } from '../../utils/DateUtilities';
import { INGESTION_JOB_STATUSES, TAGLEVELS } from '../../utils/Constants';
import CloseButton from '../ui/CloseButton';

const STATUS_VARIANT = {
    DONE: 'bg-success',
    FAILED: 'bg-danger',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Recent jobs table. Click a row to filter the drafts grid by that job_id.
// CSV ingestion is synchronous, so jobs are DONE (or FAILED) immediately — no polling.
const IngestionJobList = ({ refreshSignal, onSelectJob, onJobRemoved }) => {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();
    const callApiRef = useRef(CallApi);
    useEffect(() => { callApiRef.current = CallApi; });

    // Build the model-test quiz (TOEFL or IELTS) from a pushed job's blueprint (once its
    // drafts are approved). The build endpoint is generic — it keys off the pushed blueprint.
    const buildModelTestQuiz = async (jobId, label) => {
        const res = await CallApi({ url: getBuildToeflQuizUrl(jobId), method: 'POST', token: true });
        if (res.success) {
            const d = res.data || {};
            if (d.quiz_id) {
                showSuccess(`${label} test built (test #${d.quiz_id}) — review & publish it on Manage Tests.`);
                fetchJobs();
            } else {
                showSuccess(`Not built yet: ${(d.unresolved || []).length} question(s) still need approval.`);
            }
        }
    };

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null); // job pending removal
    const [busy, setBusy] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [levelFilter, setLevelFilter] = useState('');

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        const filters = { page, limit: pageSize };
        if (statusFilter) filters.status = statusFilter;
        if (levelFilter) filters.level = levelFilter;
        const res = await callApiRef.current({
            url: getIngestionJobsUrl(filters),
            method: 'GET',
            token: true,
        });
        if (res.success) {
            setJobs(res.data.jobs || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [page, pageSize, statusFilter, levelFilter]);

    useEffect(() => { fetchJobs(); }, [fetchJobs, refreshSignal]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // Delete the job (and its drafts). deleteQuestions=true also removes the
    // questions this job added to the bank, except any already used by a quiz.
    const doRemove = async (deleteQuestions) => {
        if (!removeTarget) return;
        setBusy(true);
        const res = await CallApi({
            url: getIngestionJobDeleteUrl(removeTarget.job_id, deleteQuestions),
            method: 'DELETE',
            token: true,
        });
        setBusy(false);
        if (res.success) {
            const d = res.data || {};
            let msg = `Job #${d.job_id} removed.`;
            if (deleteQuestions) {
                msg += ` Deleted ${d.deleted_questions} question(s)`;
                msg += d.kept_questions_in_quiz
                    ? `; kept ${d.kept_questions_in_quiz} still used by a test.`
                    : '.';
            }
            showSuccess(msg);
            const removedId = removeTarget.job_id;
            setRemoveTarget(null);
            fetchJobs();
            if (onJobRemoved) onJobRemoved(removedId);
        }
        // Errors surface via useApiCaller's ErrorContext handling; keep the modal open.
    };

    return (
        <div className="card mb-4">
            <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <h5 className="card-title mb-0 me-auto">Recent Jobs</h5>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={levelFilter}
                        onChange={e => { setLevelFilter(e.target.value); setPage(1); }}
                        title="Filter by level"
                    >
                        <option value="">All levels</option>
                        {TAGLEVELS.map(item => item.submenu
                            ? <optgroup key={item.value} label={item.label}>{item.submenu.map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}</optgroup>
                            : <option key={item.value} value={item.value}>{item.label}</option>
                        )}
                    </select>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                        title="Filter by status"
                    >
                        <option value="">All statuses</option>
                        {INGESTION_JOB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button className="btn btn-sm btn-outline-secondary" onClick={fetchJobs} disabled={loading}>
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>

                {jobs.length === 0 ? (
                    <p className="text-muted mb-0">No ingestion jobs yet.</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th style={{ width: 260 }}>Source</th>
                                    <th>Created by</th>
                                    <th>Level</th>
                                    <th>Status</th>
                                    <th title="Total drafts parsed from this source">
                                        Found
                                    </th>
                                    <th title="Drafts breakdown: pending review / approved / rejected">
                                        Drafts
                                    </th>
                                    <th>Created</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.map(j => {
                                    const isFolder = !!(j.metadata?.model_test_blueprint || j.metadata?.toefl_blueprint);
                                    const lastSeg = (j.source_uri || '').replace(/[/\\]+$/, '').split(/[/\\]/).pop() || (j.source_uri || '');
                                    return (
                                    <tr key={j.job_id}>
                                        <td>{j.job_id}</td>
                                        <td style={{ width: 260 }}>
                                            <span className="badge bg-light text-dark me-2">{isFolder ? 'Folder' : j.source_type}</span>
                                            <span className="text-muted small" style={{ wordBreak: 'break-word' }} title={j.source_uri}>
                                                {lastSeg}
                                            </span>
                                        </td>
                                        <td className="small">
                                            {j.created_by_name && (
                                                <div className="fw-semibold">{j.created_by_name}</div>
                                            )}
                                            <span className="text-muted" title={j.created_by_email}>
                                                {j.created_by_email || '—'}
                                            </span>
                                        </td>
                                        <td>{j.level}</td>
                                        <td>
                                            <span className={`badge ${STATUS_VARIANT[j.status] || 'bg-secondary'}`}>
                                                {j.status}
                                            </span>
                                        </td>
                                        <td>{j.total_questions_found}</td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                <span
                                                    className="badge bg-warning-subtle text-dark border"
                                                    title="Pending review"
                                                >
                                                    P {j.drafts_pending ?? 0}
                                                </span>
                                                <span
                                                    className="badge bg-success-subtle text-dark border"
                                                    title="Approved (copied into question bank)"
                                                >
                                                    A {j.drafts_approved ?? 0}
                                                </span>
                                                <span
                                                    className="badge bg-secondary-subtle text-dark border"
                                                    title="Rejected"
                                                >
                                                    R {j.drafts_rejected ?? 0}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-muted small">
                                            {formatBdPst(j.created_at)}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {onSelectJob && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => onSelectJob(j.job_id)}
                                                    >
                                                        View Drafts
                                                    </button>
                                                )}
                                                {(() => {
                                                    const bp = j.metadata?.model_test_blueprint || j.metadata?.toefl_blueprint;
                                                    if (!bp) return null;
                                                    const label = bp.test_kind === 'ielts_model_test' ? 'IELTS' : (bp.level || 'TOEFL');
                                                    return j.metadata?.built_quiz_id ? (
                                                        <span className="btn btn-sm btn-success disabled">
                                                            Test #{j.metadata.built_quiz_id} built
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-success"
                                                            title={`Build the ${label} model test from approved drafts`}
                                                            onClick={() => buildModelTestQuiz(j.job_id, label)}
                                                        >
                                                            Build {label} Test
                                                        </button>
                                                    );
                                                })()}
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    title="Delete this job and everything parsed from it"
                                                    onClick={() => setRemoveTarget(j)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {total > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                            Page {page} of {totalPages} — {total} total
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                            </select>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                            >Prev</button>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                            >Next</button>
                        </div>
                    </div>
                )}

                {removeTarget && (
                    <div
                        className="modal d-block"
                        tabIndex="-1"
                        role="dialog"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                    >
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Remove job #{removeTarget.job_id}</h5>
                                    <CloseButton onClick={() => setRemoveTarget(null)} disabled={busy} />
                                </div>
                                <div className="modal-body">
                                    <p>This permanently removes the job and its parsed drafts.</p>
                                    {(removeTarget.drafts_approved ?? 0) > 0 && (
                                        <p className="mb-0">
                                            This job added <strong>{removeTarget.drafts_approved}</strong>{' '}
                                            question(s) to the question bank. Keep them, or delete them too?{' '}
                                            <span className="text-muted">
                                                Questions already used in a test are always kept.
                                            </span>
                                        </p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setRemoveTarget(null)}
                                        disabled={busy}
                                    >
                                        Cancel
                                    </button>
                                    {(removeTarget.drafts_approved ?? 0) > 0 ? (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => doRemove(false)}
                                                disabled={busy}
                                            >
                                                {busy ? 'Removing…' : 'Keep questions'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={() => doRemove(true)}
                                                disabled={busy}
                                            >
                                                {busy ? 'Removing…' : 'Delete questions too'}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() => doRemove(false)}
                                            disabled={busy}
                                        >
                                            {busy ? 'Removing…' : 'Remove'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IngestionJobList;
